import React, { useEffect, useMemo } from 'react'

import {
  routes,
  useGetMyDraftsProject,
  useFolderTerm,
  useFlag,
} from '@sketch/modules-common'

import {
  Dropdown,
  LoadingPlaceholder,
  useModalContext,
  ToastLink,
  SelectDropdownSubMenu,
  useForTablet,
} from '@sketch/components'
import {
  MoveShareToProjectModal,
  RemoveShareFromProjectModal,
} from '../../../projects'
import { MoveShareToCollectionModal } from '../../../collections/modals/MoveShareToCollectionModal'
import {
  Chevron,
  SubMenuItem,
  SubMenuTrigger,
} from './ManageProjectAndCollectionActions.styles'

import { useRemoveSharesFromCollection } from '../../../collections/operations'
import { useToast } from '@sketch/toasts'

// GraphQL
import {
  useGetShareCollectionLazyQuery,
  useGetProjectCollectionsForSelectQuery,
  CollectionForSelectFragment,
  ProjectFragment,
  ShareListItemFragment,
  useGetWorkspaceQuery,
  useGetWorkspacesDraftsProjectsQuery,
} from '@sketch/gql-types'

import { ErrorHandler } from '@sketch/tracing'

import { ReactComponent as PlusIcon } from '@sketch/icons/plus-17'
import { ReactComponent as ExclamationMark } from '@sketch/icons/exclamation-mark-16'
import { CreateCollectionModal } from '../../../collections/modals'
import { MoveShareToMyDrafts } from '../../../projects/modals/MoveShareToMyDrafts'

interface Share
  extends Pick<
    ShareListItemFragment,
    | 'identifier'
    | 'name'
    | 'project'
    | 'userCanUpdateSettings'
    | 'publicAccessLevel'
    | 'shareType'
  > {}

const MoveToProjectAction: React.FC<{ share: Share }> = ({ share }) => {
  const { showModal } = useModalContext()
  return (
    <Dropdown.Item
      onClick={() =>
        showModal(MoveShareToProjectModal, {
          share,
          currentProject: share.project,
        })
      }
    >
      Move To&hellip;
    </Dropdown.Item>
  )
}

const RemoveFromProjectAction: React.FC<{
  share: Share
  workspaceIdentifier: string
}> = ({ share, workspaceIdentifier }) => {
  const { showModal } = useModalContext()

  const draftsProject = useGetMyDraftsProject(workspaceIdentifier!)

  const isInMyDrafts = share.project?.identifier === draftsProject?.identifier

  const text = isInMyDrafts ? (
    <>Remove&nbsp;from&nbsp;My&nbsp;Drafts&hellip;</>
  ) : (
    <>Remove&nbsp;from&nbsp;Project&hellip;</>
  )

  return (
    <Dropdown.Item
      onClick={() => showModal(RemoveShareFromProjectModal, { share })}
    >
      {text}
    </Dropdown.Item>
  )
}

const SingleLevelActions: React.FC<{
  share: Share
  workspaceIdentifier: string
}> = ({ share, workspaceIdentifier }) => (
  <>
    <MoveToProjectAction share={share} />
    {share.project && (
      <RemoveFromProjectAction
        workspaceIdentifier={workspaceIdentifier}
        share={share}
      />
    )}
  </>
)

export const CollectionsLoadingState = () => (
  <SubMenuItem
    text="Loading collections…"
    icon={({ className }) => (
      <LoadingPlaceholder className={className} size="17px" />
    )}
    disabled
  />
)

export const CollectionsErrorState = () => (
  <SubMenuItem
    text="Unable to load collections"
    icon={({ className }) => <ExclamationMark className={className} />}
    disabled
  />
)

interface MoveSubMenuProps {
  share: Share
  project: ProjectFragment | null
  workspaceIdentifier: string
  currentCollection?: CollectionForSelectFragment | null
}

const MoveSubMenu: React.FC<MoveSubMenuProps> = ({
  share,
  project,
  workspaceIdentifier,
  currentCollection,
}) => {
  const { showModal } = useModalContext()
  const folderTerm = useFolderTerm()
  const isNestedProjectsEnabled = useFlag('nested-projects')

  const isTabletAndBigger = useForTablet()

  const draftsProject = useGetMyDraftsProject(workspaceIdentifier)

  const { data: workspaceData } = useGetWorkspaceQuery({
    variables: { identifier: workspaceIdentifier },
  })

  const hasDraftsProject = !!draftsProject

  const { loading, error, data } = useGetProjectCollectionsForSelectQuery({
    variables: { projectIdentifier: project?.identifier ?? '' },
    skip: !project,
  })

  const isAdmin = workspaceData?.workspace.userRole === 'ADMIN'

  const { data: workspacesDraftsProjects } =
    useGetWorkspacesDraftsProjectsQuery({
      variables: { topLevel: !isNestedProjectsEnabled },
      skip: !isAdmin,
    })

  // We check if there are other Workspaces the user can move the draft to
  const workspaces = useMemo(() => {
    return (
      workspacesDraftsProjects?.me.workspaces.filter(workspace => {
        const isEditor = workspace.userCanEdit
        const isActive = workspace.status === 'ACTIVE'

        if (!isEditor || !isActive) {
          return false
        }

        return true
      }, []) ?? []
    )
  }, [workspacesDraftsProjects?.me.workspaces])

  // If there's only a single workspace available, we hide the My Drafts option
  // because the user cannot  move a my drafts project to itself
  const singleWorkspaceDraftsAvailable = workspaces.length === 1

  const handleCreateCollection = () => {
    if (!project) {
      return
    }

    showModal(CreateCollectionModal, {
      project,
      workspaceIdentifier,
      shareIdentifier: share.identifier,
    })
  }

  const collections =
    data?.project.collectionsUnpaginated
      ?.filter(({ identifier }) => identifier !== currentCollection?.identifier)
      .map(
        collection =>
          ({
            __typename: 'Collection',
            identifier: collection.identifier,
            name: collection.name,
          }) as CollectionForSelectFragment
      ) ?? []

  return (
    <SelectDropdownSubMenu
      data-hassublevels={true}
      value="Move"
      usePortal
      breakpoint="sm"
      headerItem={!isTabletAndBigger && <Dropdown.Header>Move</Dropdown.Header>}
      renderTrigger={props => (
        <SubMenuTrigger
          text="Move"
          data-testid="move-submenu"
          additional={<Chevron />}
          data-hassublevels={true}
          {...props}
        />
      )}
      items={['MY_DRAFTS', 'PROJECT', 'COLLECTION']}
      renderItem={item => {
        if (item === 'PROJECT') {
          return (
            <SubMenuItem
              text={`To ${folderTerm.upper}…`}
              data-hassublevels={true}
              onClick={() => {
                showModal(MoveShareToProjectModal, {
                  share,
                  currentProject: share.project,
                })
              }}
            />
          )
        }

        if (item === 'MY_DRAFTS') {
          const isInMyDrafts =
            share.project?.identifier === draftsProject?.identifier

          // Hide the My Drafts option if:
          // - there's not a Drafts Project
          // - the document is already in My Drafts and the user is not an Admin
          // - the document is already in My Drafts and the user is IS an Admin but there are no other workspaces to move to
          if (
            !hasDraftsProject ||
            (isInMyDrafts && !isAdmin) ||
            (isInMyDrafts && isAdmin && singleWorkspaceDraftsAvailable)
          ) {
            return
          }

          return (
            <SubMenuItem
              text="To My Drafts…"
              data-hassublevels={true}
              onClick={() => {
                showModal(MoveShareToMyDrafts, {
                  workspaceIdentifier,
                  share,
                  draftsProject,
                })
              }}
            />
          )
        }

        if (loading) {
          return <CollectionsLoadingState />
        }

        if (error) {
          return <CollectionsErrorState />
        }

        if (collections.length) {
          return (
            <SubMenuItem
              text="To Collection…"
              data-hassublevels={true}
              onClick={() => {
                if (!project) {
                  return
                }

                showModal(MoveShareToCollectionModal, {
                  share,
                  collections,
                  handleCreateCollection,
                  project,
                  workspaceIdentifier,
                  currentCollection,
                })
              }}
            />
          )
        }

        if (project && !project.topLevelProject) {
          return (
            <SubMenuItem
              onClick={handleCreateCollection}
              text="Create Collection…"
              icon={PlusIcon}
            />
          )
        }

        return null
      }}
    />
  )
}

interface RemoveSubMenuProps {
  share: Share
  project: ProjectFragment
  collection: CollectionForSelectFragment
  workspaceIdentifier: string
}

const RemoveSubMenu: React.FC<RemoveSubMenuProps> = ({
  share,
  project,
  collection,
  workspaceIdentifier,
}) => {
  const { showModal } = useModalContext()
  const { showToast } = useToast()
  const isTabletAndBigger = useForTablet()
  const folderTerm = useFolderTerm()

  const removeSharesFromCollection = useRemoveSharesFromCollection({
    projectIdentifier: project.identifier,
    onCompleted: () => {
      showToast(
        <>
          Document moved to &ldquo;
          <ToastLink
            to={routes.WORKSPACE_PROJECT.create({
              projectId: project.identifier,
              workspaceId: workspaceIdentifier,
            })}
          >
            {project.name}
          </ToastLink>
          &rdquo;
        </>
      )
    },
  })

  return (
    <SelectDropdownSubMenu
      value="Remove"
      usePortal
      breakpoint="sm"
      data-hassublevels={true}
      headerItem={
        !isTabletAndBigger && <Dropdown.Header>Remove</Dropdown.Header>
      }
      renderTrigger={props => (
        <SubMenuTrigger
          data-hassublevels={true}
          text="Remove"
          additional={<Chevron />}
          {...props}
        />
      )}
      items={['PROJECT', 'COLLECTION']}
      renderItem={item => {
        if (item === 'PROJECT') {
          return (
            <SubMenuItem
              data-hassublevels={true}
              text={`From ${folderTerm.upper}…`}
              onClick={() =>
                showModal(RemoveShareFromProjectModal, { share, collection })
              }
            />
          )
        }

        return (
          <SubMenuItem
            data-hassublevels={true}
            text="From Collection"
            onClick={() => {
              removeSharesFromCollection({
                variables: {
                  identifier: collection.identifier,
                  shareIdentifiers: [share.identifier],
                },
              })
            }}
          />
        )
      }}
    />
  )
}

interface ManageProjectAndCollectionActionsProps {
  share: Share
  workspaceIdentifier: string
}

const ManageProjectAndCollectionActions: React.FC<
  ManageProjectAndCollectionActionsProps
> = ({ share, workspaceIdentifier }) => {
  const [getCollection, { data, error, loading }] =
    useGetShareCollectionLazyQuery()

  const shareIdentifier = share.identifier

  useEffect(() => {
    getCollection({ variables: { shareIdentifier } })
  }, [getCollection, shareIdentifier])

  if (loading) {
    const loadingCount = share.project ? 2 : 1
    return <Dropdown.Skeleton count={loadingCount} />
  }

  if (error) {
    ErrorHandler.shouldNeverHappen(
      'Fetching share.collection should never fail'
    )

    return (
      <SingleLevelActions
        workspaceIdentifier={workspaceIdentifier}
        share={share}
      />
    )
  }

  const maybeProject = share.project
  const maybeCollection = data?.share?.collection

  if (maybeProject) {
    return (
      <>
        <MoveSubMenu
          share={share}
          project={maybeProject}
          workspaceIdentifier={workspaceIdentifier}
          currentCollection={maybeCollection}
        />
        {maybeCollection ? (
          <RemoveSubMenu
            share={share}
            project={maybeProject}
            collection={maybeCollection}
            workspaceIdentifier={workspaceIdentifier}
          />
        ) : (
          <RemoveFromProjectAction
            workspaceIdentifier={workspaceIdentifier}
            share={share}
          />
        )}
      </>
    )
  }

  return (
    <SingleLevelActions
      workspaceIdentifier={workspaceIdentifier}
      share={share}
    />
  )
}

export default ManageProjectAndCollectionActions
